import { onReady } from "~/foundation/Events/onReady";
import { loadAdobePDFScript } from "./loadAdobePDFScript";
import { getParameter } from "~/foundation/Helpers/queryString";
import { disableScrollLock, enableScrollLock } from "~/foundation/Dom/scrollLock";
import { addClass, hasClass, removeClass } from "~/foundation/Dom/classList";

const viewerConfig = {
	embedMode: "LIGHT_BOX",
	showDownloadPDF: false,
	showPrintPDF: false,
	defaultViewMode: hasClass(document.body, "is-app")
		? "CONTINUOUS"
		: "FIT_PAGE"
};

const ADOBE_VIEWER_CLASSES = [
	"ViewSDK_parentRelativeHeight",
	"ViewSDK_hideOverflow",
	"ViewSDK_fullScreenPDFViewer"
];

const CLOSE_BUTTON_ID = "pdf-viewer__close";

export class PdfViewer {
	/**
		 * Internal placeholder for cached DOM-objects.
		 *
		 * @type {object}
		 * @ignore
		 */
	dom = {
		container: undefined
	};

	/**
		 *
		 * @param {Element} domReference - The element to work from.
		 */
	constructor(domReference, adobeClientId) {
		this.dom.container = domReference;
		if (this.dom.container.classList.contains("button--pdf")) {
			this.dom.pdfButtons = [domReference];
		} else {
			this.dom.pdfButtons = this.dom.container.querySelectorAll("button.button--pdf[data-no-restriction]");
		}
		this.adobeClientId = adobeClientId || document.body.dataset.adobeClientId;
		this.activeWrapperId = null;
		this.escapeHandler = null;
		this.closeButtonHandler = null;
		this.focusAfterFailHandler = null;

		onReady(() => this.initialize());
	}

	activatePdfViewer(pdfModalWrapperId) {
		this.deactivatePdfViewer(this.activeWrapperId);

		enableScrollLock();
		addClass(
			document.getElementById(pdfModalWrapperId),
			"pdf-viewer__wrapper--active"
		);
		this.activeWrapperId = pdfModalWrapperId;
		this.bindEscapeToClose(pdfModalWrapperId);
		// Always available so license/error dialogs that skip PDF_VIEWER_CLOSE
		// cannot permanently trap the page under Adobe's fullscreen shell.
		this.showCloseButton(pdfModalWrapperId);
	}

	deactivatePdfViewer(pdfModalWrapperId) {
		const wrapperId = pdfModalWrapperId || this.activeWrapperId;
		const wrapper = wrapperId ? document.getElementById(wrapperId) : null;

		disableScrollLock();
		this.unbindEscapeToClose();
		this.unbindFocusAfterFail();
		this.hideCloseButton();

		if (wrapper) {
			removeClass(wrapper, "pdf-viewer__wrapper--active");
			ADOBE_VIEWER_CLASSES.forEach((className) => {
				wrapper.classList.remove(className);
			});
			// Remove Adobe iframe / leftover DOM so it cannot keep covering the page
			// after license errors or incomplete close.
			wrapper.innerHTML = "";
		}

		if (this.activeWrapperId === wrapperId) {
			this.activeWrapperId = null;
		}
	}

	bindEscapeToClose(pdfModalWrapperId) {
		this.unbindEscapeToClose();
		this.escapeHandler = (event) => {
			if (event.key === "Escape") {
				this.deactivatePdfViewer(pdfModalWrapperId);
			}
		};
		document.addEventListener("keydown", this.escapeHandler);
	}

	unbindEscapeToClose() {
		if (this.escapeHandler) {
			document.removeEventListener("keydown", this.escapeHandler);
			this.escapeHandler = null;
		}
	}

	/**
	 * After a failed render, Adobe's license dialog lives in a cross-origin iframe.
	 * Dismissing it does not fire PDF_VIEWER_CLOSE or blur the parent window, so the
	 * fullscreen shell stays. Tear down immediately when render fails instead.
	 */
	handleRenderFailed(pdfModalWrapperId) {
		this.deactivatePdfViewer(pdfModalWrapperId);
	}

	unbindFocusAfterFail() {
		if (this.focusAfterFailHandler) {
			window.removeEventListener("focus", this.focusAfterFailHandler);
			this.focusAfterFailHandler = null;
		}
	}

	showCloseButton(pdfModalWrapperId) {
		this.hideCloseButton();

		const button = document.createElement("button");
		button.type = "button";
		button.id = CLOSE_BUTTON_ID;
		button.className = "pdf-viewer__close";
		button.setAttribute("aria-label", "Close PDF viewer");
		button.innerHTML = "<span aria-hidden=\"true\">&times;</span>";

		this.closeButtonHandler = (event) => {
			event.preventDefault();
			event.stopPropagation();
			this.deactivatePdfViewer(pdfModalWrapperId);
		};
		button.addEventListener("click", this.closeButtonHandler);
		document.body.appendChild(button);
	}

	hideCloseButton() {
		const button = document.getElementById(CLOSE_BUTTON_ID);
		if (button && this.closeButtonHandler) {
			button.removeEventListener("click", this.closeButtonHandler);
		}
		this.closeButtonHandler = null;
		button?.remove();
	}

	addCallBackEvents(adobeDCView, pdfModalWrapperId) {
		const closeViewer = () => {
			this.deactivatePdfViewer(pdfModalWrapperId);
		};

		adobeDCView.registerCallback(
			// eslint-disable-next-line no-undef
			AdobeDC.View.Enum.CallbackType.EVENT_LISTENER,
			(event) => {
				if (event.type === "PDF_VIEWER_CLOSE") {
					closeViewer();
					return;
				}

				if (
					event.type === "APP_RENDERING_FAILED"
					|| event.type === "PREVIEW_RENDERING_FAILED"
				) {
					this.handleRenderFailed(pdfModalWrapperId);
				}
			},
			{ enableFilePreviewEvents: true }
		);
	}

	previewPdf(pdfModalWrapperId, pdfUrl, pdfFileName) {
		this.activatePdfViewer(pdfModalWrapperId);

		// eslint-disable-next-line no-undef
		const adobeDCView = new AdobeDC.View({
			clientId: this.adobeClientId,
			divId: pdfModalWrapperId
		});

		this.addCallBackEvents(adobeDCView, pdfModalWrapperId);

		const previewPromise = adobeDCView.previewFile(
			{
				content: {
					location: {
						url: pdfUrl
					}
				},
				metaData: { fileName: pdfFileName }
			},
			viewerConfig
		);

		if (previewPromise && typeof previewPromise.catch === "function") {
			previewPromise.catch(() => {
				this.handleRenderFailed(pdfModalWrapperId);
			});
		}
	}

	getPdfWrapperId(pdfButton) {
		if (!pdfButton) {
			return null;
		}

		const nested = pdfButton.querySelector(".pdf-viewer__wrapper");
		if (nested?.id) {
			return nested.id;
		}

		const byData = pdfButton.getAttribute("data-pdf-viewer-id");
		if (byData) {
			return byData;
		}

		const sibling = pdfButton.nextElementSibling;
		if (sibling?.classList?.contains("pdf-viewer__wrapper") && sibling.id) {
			return sibling.id;
		}

		// Shared host (EditionList / passport) — outside transformed carousels.
		const shared = document.getElementById("pdf-viewer__wrapper--default");
		if (shared) {
			return shared.id;
		}

		return null;
	}

	bindEvents() {
		const scope = this;
		document.addEventListener("adobe_dc_view_sdk.ready", function () {
			const queryPDFNum = getParameter("adobePDF")[0] || "";

			if (queryPDFNum.length) {
				const specificWrapperId = `pdf-viewer__wrapper${queryPDFNum}`;
				const numericIndex = Number(queryPDFNum);
				const pdfButton =
					document.querySelector(`.button--pdf[data-pdf-viewer-id="${specificWrapperId}"]`) ||
					document.querySelector(`.button--pdf[data-href*="adobePDF=${CSS.escape(queryPDFNum)}"]`) ||
					document.getElementById(specificWrapperId)?.closest("button.button--pdf") ||
					(Number.isFinite(numericIndex) && numericIndex > 0
						? scope.dom.pdfButtons[numericIndex - 1]
						: null);

				if (!pdfButton) {
					return;
				}

				const pdfModalWrapperId = scope.getPdfWrapperId(pdfButton) || specificWrapperId;
				const pdfUrl = pdfButton.dataset.pdf;
				const pdfFileName = pdfButton.getAttribute("aria-label");
				scope.previewPdf(pdfModalWrapperId, pdfUrl, pdfFileName);
			}

			//Need to use delegated event since more editions can be loaded dynamically
			document.addEventListener("click", function (e) {
				if (e.target) {
					const pdfButton = e.target.closest(".button--pdf");
					if (pdfButton && pdfButton.hasAttribute("data-no-restriction")) {
						const pdfFileName =
							pdfButton.getAttribute("aria-label");
						const pdfModalWrapperId = scope.getPdfWrapperId(pdfButton);
						if (!pdfModalWrapperId) {
							return;
						}

						pdfButton.blur();
						scope.previewPdf(
							pdfModalWrapperId,
							pdfButton.getAttribute("data-pdf"),
							pdfFileName
						);
					}
				}
			});
		});
	}

	initialize() {
		this.bindEvents();

		if (typeof AdobeDC === "undefined") {
			loadAdobePDFScript();
		}
	}
}
